<template>
    <group-report-template  v-model:current-report-template-id="currentReportTemplateId"
                            :template-id="templateId"
                            v-model:for-group="forGroup"
                            v-model:interval="interval"
                            v-model:ran-report="reportSuccessfullySent"
                            v-model:reload-data="reloadTable"
                            v-model:running-errors="errorSending"
                            v-model:running-report="runningReport"
                            v-model:settings="settings"
                            :can-edit="permissions.can('manage_excursion_report')"
                            :default-settings="defaultSettings"
                            :prepare-settings-function="prepareSettings"
                            :hide-title="true"
                            :use-settings-drawer="true"
                            :use-template-cards="true"
                            :use-group-reporting-option="groupReportingEnabled"
                            :has-settings="hasManualRunSettings"
                            :report-icon="settingsSchema.icon"
                            :schema="settingsSchema"
                            report-name="mkt-report"
                            v-model:display-section="displaySection"
                            @useStandardReporting="setUseStandardReporting"
                            @reset-settings="resetSelections"
    >

        <template   #create-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="true"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #edit-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="displaySection === 'show-template' || useStandardReporting"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #run-options="props">
            <!-- Explicit date range over a point selection, using the shared
                 selectors so the run form matches Excursion / Alarm Resolution.
                 The report style (reportType) is edited in the Report Settings
                 drawer and posted via extraParams. -->
            <interval-selection   :use-range="! props.reportTemplateId"
                                        v-model:range-start="rangeStart"
                                        v-model:range-end="rangeEnd"
                                        v-model:selected-meters="selectedMeters"
                                        :meters="metersByGroup[props.groupId]"
                                        :meter-tree="meterTree"
                                        :footer-mode="props.footerMode ?? 'none'"
                                        :max-date="maxDate"
                                        :min-date="minDate"
                                        :is-running="runningReport"
                                        :ran-report="reportSuccessfullySent"
                                        :running-errors="errorSending"
                                        :timezone="timezone"
                                        @run-report="runReport(props.reportTemplateId ?? null)"
            ></interval-selection>
        </template>
    </group-report-template>
</template>

<script>
import {ref} from 'vue'

import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {ajaxTableMixin} from "@/mixins/ajaxTableMixin";
import {useGroupReport} from "@/composables/useGroupReport";

import GroupReportTemplate from "./GroupReportTemplate";
import IntervalSelection from "./IntervalSelection";
import ReportSettingsPanel from "./settings/ReportSettingsPanel.vue";
import mktSettingsSchema, {MKT_CALCULATION} from "./settings/schemas/mkt";
import {toReportDate} from "./settings/ReportDateRange.vue";

export default {
    name: "GroupMktReport",
    mixins: [SonicuCoreMixin, ajaxTableMixin],
    components: {
        GroupReportTemplate,
        IntervalSelection,
        ReportSettingsPanel,
    },
    props: {
        // null on the report page; 'new' or a group report id on its New or
        // Edit page.
        templateId: {
            type: [String, Number],
            default: null,
        },
        interval: {
            type: String,
            default: 'monthly',
        },
        metersByGroup: {
            type: Object,
            default() {
                return {}
            }
        },
        meterTree: {
            type: Array,
            default() {
                return []
            }
        },
        minDate: {
            type: [Date, String],
        },
        maxDate: {
            type: [Date, String],
        },
        defaultSettings: {
            type: Object,
            default() {
                return {}
            }
        },
        timezone: {
            type: String,
        },
    },
    setup(props) {
        // Explicit start/end from the shared range selector.
        const rangeStart = ref(null)
        const rangeEnd = ref(null)

        // The composable supplies settingsContext, hasManualRunSettings, and
        // prepareSettings (weekday default); this schema has no permission-gated
        // fields. extraParams posts start/end (via toReportDate) plus the report
        // style. The pre-refactor manual run never posted reportType, so it always
        // fell to the controller default -- posting it here makes the run-form
        // style selection actually take effect. A saved template's manual run
        // posts no start, so the controller derives the window from the
        // template's interval, the same window its scheduled sends cover.
        const report = useGroupReport(mktSettingsSchema, {
            interval: props.interval,
            defaultSettings: props.defaultSettings,
            weekdayDefault: 1,
            extraParams: (state, reportTemplateId) => {
                const params = {
                    reportType: state.settings.value['reportType'] ?? MKT_CALCULATION,
                }

                if (reportTemplateId === null) {
                    params.start = toReportDate(rangeStart.value, props.timezone)
                    params.end = toReportDate(rangeEnd.value, props.timezone)
                }

                return params
            },
        })

        return {
            ...report,
            rangeStart,
            rangeEnd,
        }
    },
    methods: {
        resetSelections() {
            this.selectedMeters = []
        },
    },
    computed: {
        settingsSchema() {
            return mktSettingsSchema
        },
    },
}
</script>
